const AGREEMENT_CONFIG_URL =
  process.env.VUE_APP_AGREEMENT_CONFIG_URL || '/agreements/manifest.json';

const FALLBACK_CONFIG = {
  version: '2026-04-15',
  docs: [
    {
      key: 'productService',
      url: '/agreements/product-service-agreement-20260415.pdf',
      title: {
        zh: 'AICOIOT啤酒机SaaS系统产品服务协议',
        'zh-HK': 'AICOIOT啤酒機SaaS系統產品服務協議',
        en: 'AICOIOT Beer Machine SaaS Product Service Agreement',
        ja: 'AICOIOTビールサーバーSaaS製品サービス契約',
        es: 'Acuerdo de servicio del producto SaaS de máquina de cerveza AICOIOT',
      },
    },
    {
      key: 'sla',
      url: '/agreements/sla-detail.pdf',
      title: {
        zh: 'AICOIOT啤酒机SaaS系统服务等级协议细则',
        'zh-HK': 'AICOIOT啤酒機SaaS系統服務等級協議細則',
        en: 'AICOIOT Beer Machine SaaS Service Level Agreement',
        ja: 'AICOIOTビールサーバーSaaSサービスレベル契約',
        es: 'Acuerdo de nivel de servicio SaaS de máquina de cerveza AICOIOT',
      },
    },
    {
      key: 'privacy',
      url: '/agreements/privacy-policy.pdf',
      title: {
        zh: 'AICOIOT啤酒机SaaS系统隐私政策',
        'zh-HK': 'AICOIOT啤酒機SaaS系統隱私政策',
        en: 'AICOIOT Beer Machine SaaS Privacy Policy',
        ja: 'AICOIOTビールサーバーSaaSプライバシーポリシー',
        es: 'Política de privacidad SaaS de máquina de cerveza AICOIOT',
      },
    },
  ],
};

const AGREEMENT_STORAGE_KEY = 'aicoiot-agreement-consent';
let agreementConfigCache = null;
let agreementConfigPromise = null;

function normalizeConfig(rawConfig) {
  const payload =
    rawConfig &&
    typeof rawConfig === 'object' &&
    Number(rawConfig.status) === 200 &&
    rawConfig.data &&
    typeof rawConfig.data === 'object'
      ? rawConfig.data
      : rawConfig;
  if (!payload || typeof payload !== 'object') return FALLBACK_CONFIG;
  const version = String(payload.version || FALLBACK_CONFIG.version);
  const docs = Array.isArray(payload.docs) ? payload.docs : FALLBACK_CONFIG.docs;

  return {
    version,
    docs: docs
      .map((doc, index) => ({
        key: doc.key || `doc-${index + 1}`,
        url: doc.url || doc.file || '',
        title: doc.title || {},
      }))
      .filter((doc) => doc.url),
  };
}

import { resolveAgreementLocale, DEFAULT_LOCALE } from './locale';

export function getLocalizedText(localized, locale = 'zh-Hans-CN') {
  if (typeof localized === 'string') return localized;
  if (!localized || typeof localized !== 'object') return '';
  const agreementLocale = resolveAgreementLocale(locale);
  return (
    localized[locale] ||
    localized[agreementLocale] ||
    localized['zh-Hans-CN'] ||
    localized.zh ||
    localized['zh-Hant-HK'] ||
    localized['zh-HK'] ||
    localized['en-Latn-US'] ||
    localized.en ||
    Object.values(localized)[0] ||
    ''
  );
}

export function getAgreementConfig(forceRefresh = false) {
  if (!forceRefresh && agreementConfigCache) {
    return Promise.resolve(agreementConfigCache);
  }
  if (!forceRefresh && agreementConfigPromise) {
    return agreementConfigPromise;
  }

  const tsSeparator = AGREEMENT_CONFIG_URL.includes('?') ? '&' : '?';
  const requestUrl = `${AGREEMENT_CONFIG_URL}${tsSeparator}_t=${Date.now()}`;

  agreementConfigPromise = fetch(requestUrl, {
    method: 'GET',
    cache: 'no-store',
  })
    .then((res) => {
      if (!res.ok) throw new Error(`agreement config status ${res.status}`);
      return res.json();
    })
    .then((data) => {
      const normalized = normalizeConfig(data);
      agreementConfigCache = normalized;
      return normalized;
    })
    .catch(() => {
      agreementConfigCache = FALLBACK_CONFIG;
      return FALLBACK_CONFIG;
    })
    .finally(() => {
      agreementConfigPromise = null;
    });

  return agreementConfigPromise;
}

export function getAgreementDocs(locale = DEFAULT_LOCALE, forceRefresh = false) {
  return getAgreementConfig(forceRefresh).then((config) =>
    config.docs.map((doc) => ({
      key: doc.key,
      file: doc.url,
      title: getLocalizedText(doc.title, locale),
      titleMap: doc.title,
    }))
  );
}

export function getAgreementVersion(forceRefresh = false) {
  return getAgreementConfig(forceRefresh).then((config) => config.version);
}

function getStorage() {
  try {
    const raw = localStorage.getItem(AGREEMENT_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed ? parsed : {};
  } catch (error) {
    return {};
  }
}

function getSubject(user) {
  if (!user || typeof user !== 'object') return 'anonymous';
  return String(user.id || user.username || user.email || user.uid || 'anonymous');
}

export function hasAnyAgreementConsent(user) {
  const storage = getStorage();
  return Boolean(storage[getSubject(user)]);
}

export function shouldEnforceAgreementForUser(user) {
  if (!user || typeof user !== 'object') return false;

  const roleid = Number(
    user.roleid != null ? user.roleid : user.roleId != null ? user.roleId : NaN
  );
  if (roleid === 0 || roleid === 1) {
    return true;
  }

  const username = String(user.username || '').toLowerCase();
  const email = String(user.email || '').toLowerCase();
  return username === 'admin' || email === 'admin@themesdesign.in';
}

export function hasAcceptedLatestAgreement(user) {
  const storage = getStorage();
  const acceptedVersion = storage[getSubject(user)];
  return getAgreementVersion().then((latestVersion) => acceptedVersion === latestVersion);
}

export function saveAgreementConsent(user, version) {
  const storage = getStorage();
  storage[getSubject(user)] = String(version || FALLBACK_CONFIG.version);
  localStorage.setItem(AGREEMENT_STORAGE_KEY, JSON.stringify(storage));
}
